import { useCallback, useEffect, useState, type ComponentType } from 'react';
import { LOCALES, LOCALE_LABEL } from '@oxyflows/i18n';
import { ApiError, NetworkError, api, type Session } from './api';
import { useI18n } from './i18n';
import { DesktopBar, isDesktop } from './components/DesktopBar';
import { Loading, useDismiss } from './components';
import { FlowLine, Logo, XMark } from './brand';
import {
  IconAlert,
  IconBox,
  IconCalendar,
  IconChart,
  IconChevron,
  IconDashboard,
  IconFlask,
  IconGlobe,
  IconLogout,
  IconMenu,
  IconPatients,
  IconRx,
  IconSettings,
  IconShield,
  IconStethoscope,
  IconWallet,
} from './icons';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { Patients } from './pages/Patients';
import { Appointments } from './pages/Appointments';
import { Prescriptions } from './pages/Prescriptions';
import { Finance } from './pages/Finance';
import { Dentists } from './pages/Dentists';
import { Laboratory } from './pages/Laboratory';
import { Inventory } from './pages/Inventory';
import { Settings } from './pages/Settings';
import { Sync } from './pages/Sync';
import { Platform } from './pages/Platform';
import { Reports } from './pages/Reports';
import { setDictationMode } from './dictation/Dictation';
import { PublicClinic } from './pages/PublicClinic';
import { Policy } from './pages/Policy';

type Page =
  | 'dashboard'
  | 'patients'
  | 'appointments'
  | 'prescriptions'
  | 'finance'
  | 'dentists'
  | 'laboratory'
  | 'inventory'
  | 'clinic'
  | 'reports'
  | 'sync'
  | 'platform';

export function App() {
  const { t, locale, setLocale, formatDate } = useI18n();
  const [session, setSession] = useState<Session | null>(null);
  const [booting, setBooting] = useState(true);
  const [page, setPage] = useState<Page>('dashboard');
  const [fatal, setFatal] = useState<string | null>(null);
  const [railOpen, setRailOpen] = useState(false);
  /** Open notices from offline work, for the badge on the navigation. */
  const [attention, setAttention] = useState(0);

  useEffect(() => {
    if (!session?.activeClinicId) return;
    let alive = true;
    const load = () =>
      api
        .noticeSummary()
        .then((r) => {
          if (alive) setAttention(r.attention);
        })
        .catch(() => undefined);
    void load();
    const id = setInterval(load, 60_000);
    // The desktop status bar asks to open the list.
    const go = (e: Event) => {
      const page = (e as CustomEvent<string>).detail;
      if (page) setPage(page as Page);
    };
    window.addEventListener('oxy:navigate', go);
    return () => {
      alive = false;
      clearInterval(id);
      window.removeEventListener('oxy:navigate', go);
    };
  }, [session?.activeClinicId, page]);

  /**
   * The public clinic page at /c/{slug}.
   *
   * Rendered before any session lookup, deliberately: a visitor is never asked
   * to sign in, and nothing authenticated is fetched on their behalf (spec §9).
   */
  const publicSlug = window.location.pathname.match(/^\/c\/([a-z0-9-]+)\/?$/i)?.[1];
  /** The data policy at /policy: public, linked from sign-in and activation. */
  const policyPage = /^\/policy\/?$/i.test(window.location.pathname);

  // Restore an existing session on load. The cookie is httpOnly, so the only
  // way to know whether we are signed in is to ask the server.
  useEffect(() => {
    if (publicSlug || policyPage) {
      setBooting(false);
      return;
    }
    api
      .me()
      .then(setSession)
      .catch((e: unknown) => {
        if (e instanceof NetworkError) setFatal(t.errors.network);
        else if (!(e instanceof ApiError && e.isUnauthenticated)) setFatal(t.common.error);
      })
      .finally(() => setBooting(false));
  }, [t, publicSlug, policyPage]);

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      setSession(null);
      setPage('dashboard');
    }
  }, []);

  // Settings changed the clinic itself (name, currency, what it practises):
  // re-read the session so the navigation and the patient file follow at
  // once, without a reload. Stay on the page.
  const refreshSession = useCallback(() => {
    api.me().then(setSession).catch(() => undefined);
  }, []);

  const switchClinic = useCallback(async (clinicId: string) => {
    await api.switchClinic(clinicId);
    // Re-fetch everything: permissions, entitlement and every cached list are
    // re-derived for the new clinic rather than carried across (spec §4).
    const fresh = await api.me();
    setSession(fresh);
    setPage('dashboard');
  }, []);

  if (publicSlug) return <PublicClinic slug={publicSlug} />;
  if (policyPage) return <Policy />;

  if (booting) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', height: '100%' }}>
        <Loading />
      </div>
    );
  }

  if (fatal && !session) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', height: '100%', padding: 24 }}>
        <div className="card" style={{ maxWidth: 440 }}>
          <div className="card-body stack">
            <h2>{t.common.error}</h2>
            <p style={{ margin: 0, color: 'var(--ink-2)' }}>{fatal}</p>
            <button type="button" className="btn" onClick={() => window.location.reload()}>
              {t.common.retry}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!session) return <Login onSignedIn={setSession} />;

  const active = session.memberships.find((m) => m.clinicId === session.activeClinicId) ?? null;
  // How this clinic dictates notes (Settings, General): read by every Dictate button.
  setDictationMode(active?.dictation);
  const can = (p: string) => session.user.permissions.includes(p);
  const hasFeature = (f: string) => session.user.entitlement?.features.includes(f) ?? false;
  const isPlatform = session.user.isPlatformOwner === true;

  /*
    Signed in, but not inside a clinic.

    Two quite different people land here. A dentist who works at several clinics
    has to pick one. A PLATFORM OPERATOR usually belongs to none at all — the
    vendor is not a member of its customers' clinics — so the console is their
    whole application, not a detour from a clinic they have not chosen.
  */
  if (!session.activeClinicId || !active) {
    if (isPlatform && (page === 'platform' || session.memberships.length === 0)) {
      return (
        <div className="platform-shell">
          {isDesktop() ? <DesktopBar /> : null}
      <header className="header">
            <Logo className="platform-logo" />
            <span className="console-pill">{t.platform.title}</span>
            <div className="header-spacer" />
            <LanguageMenu locale={locale} onChange={setLocale} label={t.common.language} />
            {session.memberships.length > 0 ? (
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setPage('dashboard')}>
                {t.auth.chooseClinic}
              </button>
            ) : null}
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => void signOut()}>
              <IconLogout size={15} />
              {t.auth.signOut}
            </button>
          </header>
          <main className="main">
            <PageDeco />
            <div className="main-inner">
              <Platform />
            </div>
          </main>
        </div>
      );
    }
    return (
      <ClinicChooser
        session={session}
        onChoose={switchClinic}
        onSignOut={signOut}
        onPlatform={isPlatform ? () => setPage('platform') : undefined}
      />
    );
  }

  const clinicName = locale === 'ar' ? active.nameAr : active.nameEn;

  /*
    Access rides on the clinic's subscription. When the platform has paused
    or suspended the clinic, or the subscription has lapsed, nobody in it
    works — and everybody is told why, in their own language, rather than
    meeting a wall of failed requests.
  */
  if (active.access.state !== 'ok') {
    const reason = active.access.state;
    const until = active.access.until ?? active.access.endsAt;
    return (
      <div className="login-panel" style={{ minHeight: '100%' }}>
        <div className="login-form stack">
          <Logo className="login-logo" />
          <h1>{t.auth.blockedTitle}</h1>
          <div className="notice warn">
            <span>
              <strong>{clinicName}</strong> — {t.auth.blocked[reason]}
              {until ? ` ${t.auth.until} ${formatDate(until)}.` : ''}
            </span>
          </div>
          <p className="page-sub" style={{ margin: 0 }}>{active.isOwner ? t.auth.blockedOwnerHint : t.auth.blockedUserHint}</p>
          {session.memberships.length > 1 ? (
            <button type="button" className="btn btn-ghost" onClick={() => { setSession({ ...session, activeClinicId: null }); }}>
              {t.auth.chooseClinic}
            </button>
          ) : null}
          <button type="button" className="btn btn-ghost" onClick={() => void signOut()}>
            {t.auth.signOut}
          </button>
        </div>
      </div>
    );
  }
  const daysLeft = active.access.endsAt ? Math.ceil((new Date(active.access.endsAt).getTime() - Date.now()) / 86_400_000) : null;
  const endingSoon = daysLeft !== null && daysLeft <= 30;

  const items: { page: Page; label: string; icon: ComponentType<{ size?: number; className?: string }>; disabled?: boolean; locked?: boolean; count?: number }[] = [
    { page: 'dashboard', label: t.nav.dashboard, icon: IconDashboard },
    {
      page: 'patients',
      label: t.nav.patients,
      icon: IconPatients,
      disabled: !can('patient.read.all') && !can('patient.read.assigned'),
    },
    { page: 'appointments', label: t.nav.appointments, icon: IconCalendar, disabled: !can('appointment.read') },
    { page: 'prescriptions', label: t.rx.title, icon: IconRx, disabled: !can('rx.draft') },
    /*
      A module the clinic's PLAN does not include is marked locked rather than
      hidden, so a clinic can see what upgrading would give them. The server
      refuses those endpoints regardless of what the interface shows — the badge
      is information, not the enforcement (spec §5).
    */
    {
      page: 'finance',
      label: t.nav.finance,
      icon: IconWallet,
      disabled: !can('finance.patient_account.read') || !hasFeature('patient_finance'),
      locked: !hasFeature('patient_finance'),
    },
    {
      page: 'dentists',
      // A clinic that treats no teeth has practitioners, not dentists.
      label: active.specialties && !active.specialties.includes('dental') ? t.nav.practitioners : t.nav.dentists,
      icon: IconStethoscope,
      // Whoever books, whoever settles, whoever manages: each has a reason.
      disabled: !can('appointment.read') && !can('commission.read.all') && !can('dentist.manage') && !can('commission.settle'),
    },
    {
      page: 'laboratory',
      label: t.nav.laboratory,
      icon: IconFlask,
      disabled: (!can('lab.read') && !can('lab.finance')) || !hasFeature('laboratory_department'),
      locked: !hasFeature('laboratory_department'),
    },
    {
      page: 'inventory',
      label: t.nav.inventory,
      icon: IconBox,
      // The accountant posts bills and manages suppliers without ever
      // counting stock; the page is theirs too.
      disabled: (!can('inventory.read') && !can('supplier.manage') && !can('purchase.post')) || !hasFeature('inventory_department'),
      locked: !hasFeature('inventory_department'),
    },
    // A dentist's own figures, or the clinic's for those who run it.
    { page: 'reports', label: t.nav.reports, icon: IconChart, disabled: !can('reports.read') && !can('reports.own') },
    {
      page: 'clinic',
      label: t.nav.clinic,
      icon: IconSettings,
      disabled: !can('clinic.settings.manage') && !can('clinic.audit.read'),
    },
    // What the rules decided while a computer worked offline (ADR-0009).
    // The public demo has no computers working offline.
    ...(window.oxyDemo ? [] : [{ page: 'sync' as const, label: t.nav.sync, icon: IconAlert, count: attention }]),
  ];

  const currentLabel = page === 'platform' ? t.platform.title : (items.find((i) => i.page === page)?.label ?? t.nav.dashboard);

  return (
    <div className="app">
      <aside className={railOpen ? 'sidebar open' : 'sidebar'}>
        <div className="brand">
          <Logo className="brand-logo" />
        </div>

        <nav className="nav" aria-label={t.nav.workspace}>
          {items.map((item) => (
            <NavItem
              key={item.page}
              label={item.label}
              Icon={item.icon}
              active={page === item.page}
              disabled={item.disabled}
              locked={item.locked}
              count={item.count}
              onClick={() => {
                setPage(item.page);
                setRailOpen(false);
              }}
            />
          ))}
          {/*
            The platform console is not part of a clinic. It appears only for a
            user the database has flagged as a platform operator — a clinic
            owner, however senior, never sees it, because `clinic.staff.manage`
            refuses to grant a platform permission at all.
          */}
          {isPlatform ? (
            <NavItem
              label={t.platform.title}
              Icon={IconShield}
              active={page === 'platform'}
              onClick={() => {
                setPage('platform');
                setRailOpen(false);
              }}
            />
          ) : null}
        </nav>

        {/* The brand's line. Who is signed in, the plan and the clinic moved
            to the account menu in the header, where the references put them. */}
        <div className="sidebar-foot" aria-hidden="true">
          <FlowLine className="sidebar-flow" />
          <p className="motto">
            {t.brand.motto.map((line) => (
              <span key={line} style={{ display: 'block' }}>
                {line}
              </span>
            ))}
          </p>
          <svg className="motto-arrow" viewBox="0 0 30 12" focusable="false">
            <path d="M1 6h27M23 1.5 28 6l-5 4.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </aside>

      {isDesktop() ? <DesktopBar /> : null}
      <header className="header">
        <button
          type="button"
          className="btn btn-ghost btn-sm rail-toggle"
          onClick={() => setRailOpen((o) => !o)}
          aria-label={t.nav.workspace}
          aria-expanded={railOpen}
        >
          <IconMenu size={16} />
        </button>
        <div className="breadcrumb">
          <span>{clinicName}</span>
          <IconChevron size={13} className="breadcrumb-sep" />
          <strong>{currentLabel}</strong>
        </div>
        <div className="header-spacer" />
        <LanguageMenu locale={locale} onChange={setLocale} label={t.common.language} />
        <span className="header-divider" aria-hidden="true" />
        <AccountMenu session={session} onSwitch={switchClinic} onSignOut={signOut} />
      </header>

      <main className="main">
        <PageDeco />
        <div className="main-inner">
          {endingSoon && active.access.endsAt ? (
            <div className="notice warn" style={{ marginBottom: 16 }} role="status">
              <span>
                {(active.isOwner ? t.auth.expiresSoon : t.auth.expiresSoonUser)
                  .replace('{date}', formatDate(active.access.endsAt))
                  .replace('{days}', String(Math.max(0, daysLeft ?? 0)))}
              </span>
            </div>
          ) : null}
          {page === 'dashboard' ? (
            <Dashboard
              clinicId={session.activeClinicId}
              userName={session.user.fullName}
              permissions={session.user.permissions}
              onGo={(p) => setPage(p as Page)}
            />
          ) : page === 'patients' ? (
            <Patients
              clinicId={session.activeClinicId}
              canWrite={can('patient.write')}
              permissions={session.user.permissions}
              specialties={active.specialties ?? ['dental']}
              features={session.user.entitlement?.features ?? []}
            />
          ) : page === 'appointments' ? (
            <Appointments clinicId={session.activeClinicId} canWrite={can('appointment.write')} />
          ) : page === 'prescriptions' ? (
            <Prescriptions clinicId={session.activeClinicId} canIssue={can('rx.issue')} canManageTemplates={can('rx.template.manage')} />
          ) : page === 'finance' ? (
            <Finance clinicId={session.activeClinicId} permissions={session.user.permissions} currency={active.currency} />
          ) : page === 'dentists' ? (
            <Dentists
              clinicId={session.activeClinicId}
              permissions={session.user.permissions}
              ownMembershipId={session.user.membershipId ?? null}
            />
          ) : page === 'laboratory' ? (
            <Laboratory
              clinicId={session.activeClinicId}
              canWrite={can('lab.write')}
              permissions={session.user.permissions}
              currency={active.currency}
            />
          ) : page === 'inventory' ? (
            <Inventory
              clinicId={session.activeClinicId}
              canWrite={can('inventory.write')}
              permissions={session.user.permissions}
              currency={active.currency}
            />
          ) : page === 'reports' ? (
            <Reports permissions={session.user.permissions} />
          ) : page === 'platform' ? (
            <Platform />
          ) : page === 'sync' ? (
            <Sync currency={active.currency} onGo={(p) => setPage(p as Page)} />
          ) : (
            <Settings clinicId={session.activeClinicId} permissions={session.user.permissions} onClinicChanged={refreshSession} />
          )}
        </div>
      </main>
    </div>
  );
}

function NavItem({
  label,
  Icon,
  active = false,
  locked = false,
  disabled = false,
  count = 0,
  onClick,
}: {
  label: string;
  Icon: ComponentType<{ size?: number; className?: string }>;
  active?: boolean;
  /** The clinic's plan does not include this module. */
  locked?: boolean;
  disabled?: boolean;
  /** Things waiting for a look, shown as a badge. */
  count?: number;
  onClick?: () => void;
}) {
  const { t } = useI18n();
  return (
    <button
      type="button"
      className="nav-item"
      aria-current={active ? 'page' : undefined}
      onClick={onClick}
      disabled={disabled}
      title={locked ? t.errors.entitlement : disabled ? t.errors.permission : undefined}
    >
      <Icon size={18} className="nav-icon" />
      <span>{label}</span>
      {count > 0 ? <span className="nav-count">{count}</span> : null}
      {locked ? (
        <span className="soon" aria-label={t.errors.entitlement}>
          {t.clinic.plan}
        </span>
      ) : null}
    </button>
  );
}

/**
 * Who is signed in, where, and the clinic switcher — one control.
 *
 * The brand's references put the person and their clinic together in the
 * header's corner. Behind it: the account (email, roles, how many
 * permissions), every clinic this person belongs to with the role and plan
 * held THERE — the same person is often an owner in one and a restricted
 * dentist in the next — and signing out. The clinic you are in stays on
 * screen at all times: under your name here, and in the breadcrumb.
 */
function AccountMenu({
  session,
  onSwitch,
  onSignOut,
}: {
  session: Session;
  onSwitch: (id: string) => Promise<void>;
  onSignOut: () => Promise<void>;
}) {
  const { t, locale } = useI18n();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useDismiss(open, () => setOpen(false));
  const active = session.memberships.find((m) => m.clinicId === session.activeClinicId);
  const clinicName = active ? (locale === 'ar' ? active.nameAr : active.nameEn) : '';
  const many = session.memberships.length > 1;

  return (
    <div className="menu-wrap" ref={ref}>
      <button
        type="button"
        className="account-button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="menu"
        disabled={busy}
        title={many ? t.clinic.switch : undefined}
      >
        <span className="avatar">{initials(session.user.fullName)}</span>
        <span className="account-text">
          <strong>{session.user.fullName}</strong>
          <small>{clinicName}</small>
        </span>
        <IconChevron size={14} className="account-caret" />
      </button>
      {open ? (
        <div className="menu account-menu" role="menu">
          <div className="menu-label">{session.user.fullName}</div>
          <div className="account-meta">
            {session.user.email}
            <br />
            {session.user.roles.join(', ')} · {session.user.permissions.length} {t.staff.permissions}
            {active ? (
              <>
                <br />
                {t.clinic.plan}: {active.plan}
              </>
            ) : null}
          </div>
          {many ? (
            <>
              <div className="menu-sep" />
              <div className="menu-label">{t.clinic.switch}</div>
              {session.memberships.map((m) => {
                const label = locale === 'ar' ? m.nameAr : m.nameEn;
                return (
                  <button
                    key={m.clinicId}
                    type="button"
                    role="menuitem"
                    className="clinic-menu-item"
                    aria-current={m.clinicId === session.activeClinicId}
                    disabled={busy}
                    onClick={async () => {
                      setOpen(false);
                      if (m.clinicId === session.activeClinicId) return;
                      setBusy(true);
                      try {
                        await onSwitch(m.clinicId);
                      } finally {
                        setBusy(false);
                      }
                    }}
                  >
                    <span className="clinic-mark">{initials(label)}</span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', fontWeight: 500 }}>{label}</span>
                      <span style={{ display: 'block', color: 'var(--ink-3)', fontSize: 12 }}>{m.roles.join(', ')}</span>
                    </span>
                    <span className="badge brand">{m.plan}</span>
                  </button>
                );
              })}
            </>
          ) : null}
          <div className="menu-sep" />
          <button type="button" role="menuitem" className="menu-item" onClick={() => void onSignOut()}>
            <IconLogout size={15} />
            {t.auth.signOut}
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** The brand's X and a flow line in the corner of every page. Decorative. */
function PageDeco() {
  return (
    <div className="page-deco" aria-hidden="true">
      <XMark className="page-deco-x" />
      <FlowLine className="page-deco-line" />
    </div>
  );
}

/** Somebody signed in who has not picked a clinic yet. */
function ClinicChooser({
  session,
  onChoose,
  onSignOut,
  onPlatform,
}: {
  session: Session;
  onChoose: (id: string) => Promise<void>;
  onSignOut: () => Promise<void>;
  onPlatform?: () => void;
}) {
  const { t, locale } = useI18n();
  return (
    <div className="login-panel" style={{ minHeight: '100%' }}>
      <div className="login-form stack">
        <Logo className="login-logo" />
        <h1>{session.memberships.length > 0 ? t.auth.chooseClinic : t.platform.title}</h1>
        <p className="page-sub" style={{ margin: 0 }}>
          {/* An operator who belongs to no clinic is not "choosing" one. */}
          {session.memberships.length > 0 ? t.auth.chooseClinicHint : t.platform.whoNote}
        </p>
        <div className="demo-list">
          {session.memberships.map((m) => (
            <button key={m.clinicId} type="button" className="demo-account" onClick={() => void onChoose(m.clinicId)}>
              <span>
                <strong>{locale === 'ar' ? m.nameAr : m.nameEn}</strong>
                <span className="who" style={{ display: 'block' }}>
                  {m.roles.join(', ')}
                </span>
              </span>
              {m.access.state === 'ok' ? (
                <span className="badge brand">{m.plan}</span>
              ) : (
                <span className="badge warn">{t.auth.blocked[m.access.state]}</span>
              )}
            </button>
          ))}
        </div>
        {onPlatform ? (
          <button type="button" className="btn btn-ghost" onClick={onPlatform}>
            {t.platform.title}
          </button>
        ) : null}
        <button type="button" className="btn btn-ghost" onClick={() => void onSignOut()}>
          {t.auth.signOut}
        </button>
      </div>
    </div>
  );
}

function LanguageMenu({
  locale,
  onChange,
  label,
}: {
  locale: string;
  onChange: (l: 'en' | 'ar') => void;
  label: string;
}) {
  const other = LOCALES.find((l) => l !== locale) ?? 'en';
  return (
    <button
      type="button"
      className="btn btn-ghost btn-sm"
      onClick={() => onChange(other)}
      aria-label={label}
      title={label}
    >
      <IconGlobe size={15} />
      {LOCALE_LABEL[other]}
    </button>
  );
}

/**
 * Initials for an avatar.
 *
 * Arabic names are taken from their own first letters, not transliterated —
 * a Latin initial on an Arabic name is a small thing that reads as carelessness
 * to the person it belongs to.
 */
export function initials(name: string): string {
  const parts = name
    .replace(/^(Dr|Dr\.|د\.|الدكتور)\s+/i, '')
    .split(/\s+/)
    .filter(Boolean);
  return parts
    .slice(0, 2)
    .map((p) => [...p][0] ?? '')
    .join('');
}
